import {
  type QuizAdditionalCategory,
  type QuizQuestionChoice,
  type QuizQuestionChoiceOption,
} from '@efdigital/ll-addon-data';
import { uniqueId } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

import type { QuizQuestionScoringEditorComponentSignature } from '../../types/question-manager';
import { CategoryScores } from './shared/category-scores.gts';
import { ScoringWizard } from './shared/scoring-wizard.gts';

function not(value: boolean) {
  return !value;
}

export class MultipleChoiceScoringEditorComponent extends Component<QuizQuestionScoringEditorComponentSignature> {
  get questionData(): QuizQuestionChoice {
    return this.args.question.choice!;
  }

  get isScoreIndividually() {
    return this.questionData.scoring === 'individual';
  }

  setCorrectAnswerCategoryScores = (
    score: number,
    category: string,
    additionalCategories: QuizAdditionalCategory[]
  ) => {
    this.questionData.score = score;
    this.questionData.category = category;
    this.questionData.additionalCategories = additionalCategories;
  };

  setOptionCategoryScores = (
    option: QuizQuestionChoiceOption,
    score: number,
    category: string,
    additionalCategories: QuizAdditionalCategory[]
  ) => {
    option.score = score;
    option.category = category;
    option.additionalCategories = additionalCategories;
  };

  setScoring = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.questionData.scoring = target.value;
  };

  setCorrectAnswer = (option: QuizQuestionChoiceOption, event: Event) => {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    const choices = [...this.questionData.choices];
    choices.forEach((choice) => {
      if (choice.oid === option.oid) {
        choice.correct = isChecked;
      }
    });

    this.questionData.choices = choices;
  };

  get correctAnswer(): QuizQuestionChoiceOption | null {
    const option = this.questionData.choices.find((option) => option.correct);
    if (!option?.oid) {
      return null;
    }
    return option;
  }

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      <div class="card-body">
        <div class="form-label">If the user provides an answer, they will be
          scored as follows:</div>

        <div class="mb-4">
          <div class="checkbox">
            <label>
              <input
                id="{{uniqueId}}-scoring-individual"
                type="radio"
                value="individual"
                checked={{this.isScoreIndividually}}
                {{on "change" this.setScoring}}
                class="form-check-input"
              />
              Each option has a different score</label>
          </div>

          <div class="checkbox">
            <label>
              <input
                id="{{uniqueId}}-scoring-exact"
                type="radio"
                value="exact"
                checked={{not this.isScoreIndividually}}
                {{on "change" this.setScoring}}
                class="form-check-input"
              />
              There is a specific correct answer
            </label>
          </div>
        </div>

        {{#if this.isScoreIndividually}}
          <div class="mb-3">
            <ScoringWizard
              @questionData={{this.questionData}}
              @quiz={{@quiz}}
            />
            <ScoringWizard
              @questionData={{this.questionData}}
              @quiz={{@quiz}}
            />
          </div>
          {{#each this.questionData.choices as |option|}}
            <h5 class="mt-1 mb-2">{{option.value}}</h5>
            <CategoryScores
              @quiz={{@quiz}}
              @category={{option.category}}
              @score={{option.score}}
              @additionalCategories={{option.additionalCategories}}
              @onChange={{fn this.setOptionCategoryScores option}}
            />
          {{/each}}
        {{else}}
          <div class="mb-3">
            <label for="{{uniqueId}}-correct-answer">The correct options are:</label>
            {{#each this.questionData.choices as |choice|}}
              <div class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="{{uniqueId}}-correct-answer-{{choice.oid}}"
                  value={{choice.oid}}
                  checked={{choice.correct}}
                  {{on "change" (fn this.setCorrectAnswer choice)}}
                />
                <label
                  class="form-check-label"
                  for="{{uniqueId}}-correct-answer-{{choice.oid}}"
                >{{choice.value}}</label>
              </div>
            {{/each}}
          </div>

          <div class="form-label">If the user selects all the correct options,
            their score will be:</div>

          <CategoryScores
            @quiz={{@quiz}}
            @category={{this.questionData.category}}
            @score={{this.questionData.score}}
            @additionalCategories={{this.questionData.additionalCategories}}
            @onChange={{this.setCorrectAnswerCategoryScores}}
          />
        {{/if}}
      </div>
    {{/let}}
  </template>
}
